/**
 * Single source of truth for the four cement types surfaced in the UI.
 * Used by App.jsx routes and Sidebar.jsx so they never drift.
 */

export const CEMENT_TYPES = [
  {
    slug:        'opc',
    cementType:  'OPC',
    displayName: 'Ordinary Portland Cement 33,43,53 Grades',
    hasGrade:    true,   // only OPC (IS 269) carries a strength grade (OPC 33/43/53)
    acceptanceStandard: 'IS 269:2015 (RA 2020)',
  },
  {
    slug:        'ppc-calcined-clay',
    cementType:  'PPC_CALCINED_CLAY',
    displayName: 'Portland Pozzolana Cement (Calcined Clay Based)',
    hasGrade:    false,
    acceptanceStandard: 'IS 1489 (Part 2):2015',
  },
  {
    slug:        'ppc-fly-ash',
    cementType:  'PPC_FLY_ASH',
    displayName: 'Portland Pozzolana Cement (Fly Ash Based)',
    hasGrade:    false,
    acceptanceStandard: 'IS 1489 (Part 1):2015',
  },
  {
    slug:        'psc',
    cementType:  'PSC',
    displayName: 'Portland Slag Cement',
    hasGrade:    false,
    acceptanceStandard: 'IS 455:2015',
  },
];

/** Look up a cement type entry by URL slug. Returns undefined when not found. */
export function cementTypeBySlug(slug) {
  return CEMENT_TYPES.find((ct) => ct.slug === slug);
}

/** Look up a cement type entry by the backend enum name. Returns undefined when not found. */
export function cementTypeByEnum(cementType) {
  return CEMENT_TYPES.find((ct) => ct.cementType === cementType);
}

/** Look up a cement type entry by its Material.name (exact, trimmed, case-insensitive). */
export function cementTypeByMaterialName(name) {
  if (!name) return undefined;
  const trimmed = String(name).trim().toLowerCase();
  return CEMENT_TYPES.find((ct) => ct.displayName.toLowerCase() === trimmed);
}

/**
 * True when the given cement (by backend enum name) carries a strength grade.
 * Only OPC does. Unknown/blank types default to true so OPC behaviour is the safe fallback.
 */
export function cementTypeHasGrade(cementType) {
  if (!cementType) return true;
  const entry = cementTypeByEnum(cementType);
  return entry ? entry.hasGrade : true;
}

/** True when a cement Material.name corresponds to a graded cement (OPC only). */
export function materialNameHasGrade(name) {
  const entry = cementTypeByMaterialName(name);
  // Unknown material names default to graded (OPC-style) — matches the backend's OPC fallback.
  return entry ? entry.hasGrade : true;
}
